/* global BigInt */
import { createContext, useRef, useContext, useState, useEffect, useMemo } from 'react';
import { useInventory, useAvatarStats } from '../hooks';
import { BASEJUMP_CONTRACT_ADDRESS } from '../constants';
import BasejumpContractABI from '../contract.abi'
import { useReadContracts, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { useDisconnect, useConfig } from 'wagmi';
import { signMessage } from '@wagmi/core';
import { setAvatarEquipmentItems, addUserWallets, getUnreadMessages } from './BasejumpApiProvider';
import { AuthContext } from './AuthProvider';
import { DiscordSdkContext } from './DiscordSdkProvider';
import { parseEarnings } from '../utils';
import { useEarningsStore } from '../store/gameEarnings.store';
import { safeGtagEvent } from './AnalyticsProvider';

export const AppContext = createContext({});

const EQUIPMENT_UPDATE_DEBOUCE = 1000;

export const AppContextProvider = ({
  children,
}) => {
  const [readySessionId, setReadySessionId] = useState(null);
  const [loadedSessionId, setLoadedSessionId] = useState(null);
  const [gameSessionData, setGameSessionData] = useState(null);
  const [homepageSelectedAvatarId, setHomepageSelectedAvatarId] = useState(null);
  const { basejumpToken, setUser, user } = useContext(AuthContext);
  const [showPointsPopup, setShowPointsPopup] = useState(false);
  const [showKitchen, setShowKitchen] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedBroadsider, setSelectedBroadsider] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedShopAsset, setSelectedShopAsset] = useState(null);
  const [transferAsset, setTransferAsset] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [errorData, setErrorData] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [showDesktopOnly, setShowDesktopOnly] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [ownedBroadsiders, setOwnedBroadsiders] = useState([]);
  const [metaverseItems, setMetaverseItems] = useState([]);
  const [promoItems, setPromoItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [voiceoverApiKey, setVoiceoverApiKey] = useState('');
  const [voices, setVoices] = useState([]);
  const [inventoryUpdated, setInventoryUpdated] = useState('');
  const [moonpayProvider, setMoonpayProvider] = useState(null);
  const [moonpaySigner, setMoonpaySigner] = useState(null);
  const [moonpaySdk, setMoonpaySdk] = useState(null);
  const [accountAddress, setAccountAddress] = useState(null);
  const [walletAddress, _setWalletAddress] = useState(null);
  const [showMintModal, setShowMintModal] = useState(false);
  const [mapSrc, setMapSrc] = useState(null);
  const [isMintingInProgress, setMintingInProgress] = useState(false);
  const [mintFigures, setMintFigures]  = useState(1);
  const [priceWei, setPriceWei] = useState(0n);
  const [completeReceipt, setCompleteReceipt] = useState(null);
  const { disconnect } = useDisconnect();
  const [showUpload, setShowUpload] = useState(false);
  const { isDiscord } = useContext(DiscordSdkContext);
  const [isGamePaused, setGamePaused] = useState(false);  
  const addToEarnings = useEarningsStore((state) => state.addToEarnings);
  
  const messagesRead = useRef(false);
  useEffect(() => {
    if (!basejumpToken || messagesRead.current) {
      return;
    }
    (async () => {
      const REWARD_MESSAGE_TYPES = ['level_complete', 'order_complete'];
      const messageData = await getUnreadMessages(basejumpToken);
      messagesRead.current = true;
      const messages = messageData.map(m => m.message_content);
      const newEarnings = messages.filter(m => REWARD_MESSAGE_TYPES.includes(m.type)).map(r => parseEarnings(r, isDiscord));
      addToEarnings(...newEarnings);
    })();
  }, [basejumpToken, isDiscord]);

  const setWalletAddress = async (address) => {
    try {
      if (address) {
        const signature = await signMessage(wagmiConfig, { message: address });
        const userWithWallets = await addUserWallets(basejumpToken, [{ type: 'evm', address, signature }]);
        setUser(userWithWallets);
      }
    } catch (e) {
      setErrorData({ title: 'Error', description: 'Failed to connect wallet!' });
    } finally {
      disconnect();
    }
  };
  
  const setArWalletAddress = async (address) => {
    try {
      if (address) {
        const publicKey = await window.arweaveWallet.getActivePublicKey();
        const signatureBytes = await window.arweaveWallet.signMessage(new TextEncoder().encode(JSON.stringify({ message: address })));
        const signature = Buffer.from(signatureBytes).toString('base64');
        const userWithWallets = await addUserWallets(basejumpToken, [{ type: 'wander', address, signature: `${signature}:${publicKey}` }]);
        setUser(userWithWallets);
      }
    } catch (e) {
      setErrorData({ title: 'Error', description: 'Failed to connect AR wallet!' });
    } finally {
      disconnect();
    }
  };

  const inventory = useInventory(inventoryItems);
  const { data: readData, isLoading: isReadLoading, refetch: reloadContract } = useReadContracts({
    contracts: [
      { address: BASEJUMP_CONTRACT_ADDRESS, abi: BasejumpContractABI, functionName: 'tokenPrice' },
      { address: BASEJUMP_CONTRACT_ADDRESS, abi: BasejumpContractABI, functionName: 'totalSupply' }
    ]
  });
  const { stats: avatarStats, tempModifiers: avatarStatsModifiers } = useAvatarStats(inventoryItems, selectedBroadsider);
  const { data: hash, error, writeContract, isPending } = useWriteContract();
  const { data: receipt, isLoading: isConfirming, error: transactionError, isSuccess: isConfirmed } = useWaitForTransactionReceipt({ hash });
  const wagmiConfig = useConfig();

  useEffect(() => {
    if (error) {
      setErrorData({ title: 'Error', description: error.shortMessage });
    }
  }, [error])

  useEffect(() => {
    setMintingInProgress(isPending || isConfirming);
  }, [isPending, isConfirming]);

  useEffect(() => {
    if (transactionError) {
      setErrorData({ title: 'Error', description: transactionError.shortMessage });
    }
  }, [transactionError])

  useEffect(() => {
    if (receipt) {
      setCompleteReceipt(receipt);
      reloadContract();
    }
  }, [receipt])

  useEffect(() => {
    if (readData) {
      setPriceWei(readData[0].result);
    }
    
  }, [readData, isReadLoading]);  

  const selectBroadsider = (figure) => {
    safeGtagEvent('select_avatar', { avatar: { id: figure.getId(), name: figure.getName() }, user });
    if (figure.getId() !== selectedBroadsider.getId()) {
      //setInventoryItems([]);
      setSelectedBroadsider(figure);
    }
  };
  
  const unequipItems = (items) => {
  }

  const equipItem = async (item, slot, equip = true) => {
    inventoryItems.filter((it) => it.slot === slot).forEach((it) => {
      it.equipped = false;
      it.slot = null;
    });
    // If the item is not weapon and we are equipping, make sure we unequip all the other items of the same type
    if (equip && item.getType() !== 'weapon') {
      inventoryItems.filter((it) => it.getType() === item.getType()).forEach((it) => {
        it.equipped = false;
        it.slot = null;
      });
    }

    item.equipped = equip;
    item.slot = equip ? slot : null;

    setInventoryItems(inventoryItems);

    // Update backend equipment in background
    const equipment = inventoryItems.filter(i => i.equipped && i.slot).map(i => ({ id: i.getItemId(), slot: i.slot }));
    setAvatarEquipmentItems(selectedBroadsider.getAvatarId(), equipment, basejumpToken);
    setInventoryUpdated(`${new Date().getTime()}`);
  };

  const requestMinting = (amountToMint = 1, ogPasses = []) => {
    writeContract({
      address: BASEJUMP_CONTRACT_ADDRESS,
      abi: BasejumpContractABI,
      functionName: 'multiMint',
      args: [BigInt(amountToMint), ogPasses],
      value: BigInt(priceWei) * (BigInt(amountToMint) + BigInt(ogPasses.length))
    });
  };

  const value = useMemo(
    () => ({
      moonpayProvider,
      moonpaySigner,
      moonpaySdk,
      setMoonpayProvider,
      setMoonpaySdk,
      setMoonpaySigner,
      accountAddress,
      setAccountAddress,
      walletAddress,
      setWalletAddress,
      setArWalletAddress,
      ownedBroadsiders,
      setOwnedBroadsiders,
      selectedBroadsider,
      setSelectedBroadsider,
      selectBroadsider,
      promoItems,
      setPromoItems,
      metaverseItems,
      setMetaverseItems,
      inventoryItems,
      setInventoryItems,
      inventory,
      setSelectedAsset,
      setSelectedShopAsset,
      selectedAsset,
      selectedShopAsset,
      setTransferAsset,
      transferAsset,
      setQrCodeData,
      qrCodeData,
      setErrorData,
      errorData,
      setConfirmData,
      confirmData,
      setShowDesktopOnly,
      showDesktopOnly,
      setShowLogin,
      showLogin,
      setShowVideo,
      showVideo,
      setShowLogout,
      showLogout,
      setVoiceoverApiKey,
      voiceoverApiKey,
      voices,
      setVoices,
      equipItem,
      unequipItems,
      requestMinting,
      inventoryUpdated,
      showMintModal,
      setShowMintModal,
      mapSrc,
      setMapSrc,
      isMintingInProgress,
      setMintingInProgress,
      mintFigures,
      setMintFigures,
      priceWei,
      setPriceWei,
      completeReceipt,
      setCompleteReceipt,
      isReadLoading,
      readData,
      reloadContract,
      wagmiConfig,
      setShowUpload,
      showUpload,
      setShowSelector,
      showSelector,
      avatarStats,
      avatarStatsModifiers,
      syncResults,
      setSyncResults,
      showKitchen,
      setShowKitchen,
      showShop,
      setShowShop,
      showPointsPopup,
      setShowPointsPopup,
      isGamePaused,
      setGamePaused,
      homepageSelectedAvatarId,
      setHomepageSelectedAvatarId,
      gameSessionData,
      setGameSessionData,
      readySessionId,
      setReadySessionId,
      loadedSessionId,
      setLoadedSessionId
    }),
    [
      selectedBroadsider,
      inventoryUpdated, 
      selectedAsset,
      selectedShopAsset,
      transferAsset,
      ownedBroadsiders, 
      promoItems, 
      metaverseItems, 
      inventoryItems, 
      inventory,
      qrCodeData, 
      errorData,
      confirmData,
      showLogin, 
      showDesktopOnly, 
      voiceoverApiKey, 
      voices, 
      moonpayProvider, 
      moonpaySigner, 
      accountAddress,
      moonpaySdk, 
      showLogout, 
      showVideo,
      showMintModal,
      mapSrc,
      mintFigures,
      priceWei,
      isReadLoading,
      readData,
      isMintingInProgress,
      completeReceipt,
      wagmiConfig,
      walletAddress,
      showUpload,
      showSelector,
      avatarStats,
      avatarStatsModifiers,
      syncResults,
      showKitchen,
      showShop,
      showPointsPopup,
      isGamePaused,
      homepageSelectedAvatarId,
      gameSessionData,
      readySessionId,
      loadedSessionId
    ]
  );

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
};
